import React, { useEffect, useState, useRef } from "react";
import "aos/dist/aos.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BannerSearch from "../components/BannerSearch";
import BreadcrumbHeader from "../components/BreadcrumbHeader";
import PropertyHeading from "../components/PropertyHeading";
import Gallery from "../components/Gallery";
import RightSidebar from "../components/RightSidebar";
import Feedback from "../components/Feedback";
import PropertyTabs from "../components/PropertyTabs";
import ReviewSummaryWidget from "../components/ReviewSummaryWidget";
import { computeReviewStats } from "../utils/reviewStats";
import PropertyRooms from "../components/PropertyRooms";
import PropertyRules from "../components/PropertyRules";
import SimilarProperties from "../components/SimilarProperties";
import { useParams } from "react-router-dom";
import API_BASE_URL from "../../src/config";
import "../css/property-details.css";
import { ClipLoader } from "react-spinners";
import ChatModal from "../components/ChatModal";
import { toast } from "react-toastify";
import { formatLocalDate } from "../utils/dateUtils";
import JsonLd from "../components/JsonLd";
import PageMeta from "../components/PageMeta";

const PropertyDetails = () => {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const roomsRef = useRef(null);
  const [showChat, setShowChat] = useState(false);
  const [chatId, setChatId] = useState(null);



  // Add these here
  const [activeTab, setActiveTab] = useState("overview");
  const roomsHeadingRef = useRef(null);
  const tabsSectionRef = useRef(null);
  // console.log(property);
  const currentUser = JSON.parse(localStorage.getItem("user"));
  const currentUserId = currentUser?.id || null;
  const currentUserfName = currentUser?.firstName || "";
  const currentUserlName = currentUser?.lastName || "";

  const getChatId = (userId, hostId) =>
    [userId, hostId].sort().join("_");
  useEffect(() => {
    const fetchProperty = async () => {
  try {
    const params = JSON.parse(localStorage.getItem("searchParams") || "{}");

    //. DEFAULT DATES IF NOT AVAILABLE
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    const formatDate = formatLocalDate;

    const res = await fetch(`${API_BASE_URL}stays/details-room-combination`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        stay_id: id,
        adults: params?.adults || 1,
        kids: params?.kids || 0,
        rooms_required: params?.rooms_required || 1,

        //. FIX HERE
        start_date: params?.checkin_date || formatDate(today),
        end_date: params?.checkout_date || formatDate(tomorrow),
      }),
    });

    const json = await res.json();

    if (json.success) {
      const data = json.data;
      data.price_per_night = data.price_per_night || 0;
      setProperty(data);

      if (typeof window.fbq === "function") {
        window.fbq("track", "ViewContent", {
          content_ids: [data.id],
          content_type: "product",
          content_name: data.title,
          value: Number(data.price_per_night) || 0,
          currency: "INR",
        });
      }
    } else {
      setError(json.message);
    }
  } catch (err) {
    console.error(err);
    setError("Failed to load property details");
  } finally {
    setLoading(false);
  }
};

    fetchProperty();

    // #reviews (used by the post-checkout "Leave a Review" email link) opens
    // straight to the Reviews tab and scrolls there instead of the usual
    // scroll-to-top, so a guest coming from that email lands exactly where
    // they can leave a review, not back at the gallery.
    if (window.location.hash === "#reviews") {
      setActiveTab("reviews");
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

  }, [id]);

  useEffect(() => {
    if (window.location.hash === "#reviews" && activeTab === "reviews" && tabsSectionRef.current) {
      const yOffset = -80;
      const y = tabsSectionRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }, [activeTab, property]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100%",
        }}
      >
        <ClipLoader size={45} />
      </div>
    );
  }
  if (error) return <p>{error}</p>;

  const scrollToRooms = () => {
  if (roomsHeadingRef.current) {
    const yOffset = -80;

    const y =
      roomsHeadingRef.current.getBoundingClientRect().top +
      window.pageYOffset +
      yOffset;

    window.scrollTo({
      top: y,
      behavior: "smooth",
    });
  }
};

  const reviewStats = computeReviewStats(property?.reviews);

  // Same scroll mechanism the #reviews hash deep-link already uses (see the
  // effect above), triggered on demand from the review widget's "See all
  // reviews" link instead of a URL hash — setTimeout(0) lets the reviews
  // tab content render first so tabsSectionRef's position is accurate.
  const goToReviewsTab = () => {
    setActiveTab("reviews");
    setTimeout(() => {
      if (tabsSectionRef.current) {
        const yOffset = -80;
        const y = tabsSectionRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }, 0);
  };

  const lodgingBusinessSchema = property && {
    "@context": "https://schema.org",
    "@type": "LodgingBusiness",
    name: property.title,
    description: property.tagline || undefined,
    image: property.cover_image || undefined,
    address: {
      "@type": "PostalAddress",
      streetAddress: property.address,
      addressLocality: property.Location?.location_name,
      addressCountry: "IN",
    },
    priceRange: property.price_per_night ? `₹${property.price_per_night}` : undefined,
    // homestay_average_rating is "0.0" (a non-empty, truthy string) as a
    // placeholder when there are zero real reviews — so it can't be used as
    // the guard on its own, or every property with no reviews yet gets a
    // fake AggregateRating emitted (real bug: Google flagged exactly this,
    // a "1 review, 0.0 rating" that doesn't correspond to any actual
    // review). Only emit this block when there's at least one genuine
    // review with a real positive rating, and report the real count.
    ...(property.reviews?.length > 0 &&
      Number(property.homestay_average_rating) > 0 && {
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: property.homestay_average_rating,
          reviewCount: property.reviews.length,
        },
      }),
  };

  const propertyBreadcrumbSchema = property && {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.guestlinkup.com/" },
      { "@type": "ListItem", position: 2, name: "Stays", item: "https://www.guestlinkup.com/stays" },
      { "@type": "ListItem", position: 3, name: property.title, item: window.location.href },
    ],
  };

  return (
    <>
      {property && (
        <PageMeta
          title={`${property.title}${property.Location?.location_name ? `, ${property.Location.location_name}` : ""} - GuestLinkup`}
          description={
            property.tagline ||
            (property.description ? property.description.slice(0, 155) : undefined)
          }
          image={property.cover_image || undefined}
        />
      )}
      <Header />
      <BannerSearch />
      {property && (
        <>
          <JsonLd id="lodging-business" schema={lodgingBusinessSchema} />
          <JsonLd id="property-breadcrumb" schema={propertyBreadcrumbSchema} />
        </>
      )}
      <section className="propertydltcontainer">
        <div className="container">
          <BreadcrumbHeader
            location={property?.Location.location_name}
            title={property?.title}
            locationId={property?.Location.id}
          />
          <PropertyHeading
            rating={property?.homestay_average_rating}
            reviewStats={reviewStats}
            title={property?.title}
            tagline={property?.tagline}
            address={property?.address}
            price={property?.price_per_night}
            location={property?.Location}
            onSelectRoom={scrollToRooms}
          />
          <div className="row gx-2 mb-3 mb-md-5">
            <div className="col-lg-9">
              <Gallery galleries={property?.galleries} coverImage={property?.cover_image} />
            </div>
            <div className="col-lg-3">
              <ReviewSummaryWidget
                reviewStats={reviewStats}
                reviews={property?.reviews}
                onSeeAllReviews={goToReviewsTab}
              />
            </div>
          </div>
          <div className="row gx-2 mb-4" ref={tabsSectionRef}>
            <div className="col-xxl-9 col-xl-9 col-lg-9 col-md-12 col-sm-12 col-12">
              <PropertyTabs property={property} activeTab={activeTab} setActiveTab={setActiveTab} roomsRef={roomsRef} reviewStats={reviewStats} />
            </div>
            <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-12 col-sm-12 col-12">
              <RightSidebar property={property} />
            </div>
            <div className="col-12 text-end">
              <button
                className="theme-btn chatbtn"
                onClick={() => {
                  const currentUser = JSON.parse(localStorage.getItem("user"));
                  if (!currentUser) {
                    toast.warning("Please login to chat with host");
                    return;
                  }

                  const id = getChatId(currentUser?.id, property?.owner_id);
                  setChatId(id);
                  setShowChat(true);
                }}
              >
                <span className="chaticon"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-messages-square-icon lucide-messages-square"><path d="M16 10a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 14.286V4a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" /><path d="M20 9a2 2 0 0 1 2 2v10.286a.71.71 0 0 1-1.212.502l-2.202-2.202A2 2 0 0 0 17.172 19H10a2 2 0 0 1-2-2v-1" /></svg></span>
                <span className="p-0">Chat with Host</span>
              </button>
            </div>
          </div>

          {/* Rooms */}
          <PropertyRooms
            rooms={property?.rooms}
            stayData={property}
            headingRef={roomsHeadingRef}
            bestCombination={property?.best_combination}
            allCombinations={property?.all_combinations}
          />
          <PropertyRules rules={property?.property_rules} />
          <Feedback reviews={property?.reviews} />
          {property?.similar && property?.similar.length > 0 && (
            <SimilarProperties similar={property?.similar} />
          )}

        </div>
        {showChat && (
          <ChatModal
            chatId={chatId}
            hostId={property?.owner_id}
            onClose={() => setShowChat(false)}
            ownerfName={property?.owner?.firstname}
            ownerlName={property?.owner?.lastname}
            currentUserfName={currentUserfName}
            currentUserlName={currentUserlName}
          />
        )}

      </section>

      <Footer />
    </>
  );
};

export default PropertyDetails;
